/* ExposureSnapshotStatus.js */

"use strict";

/**
 * Is the configuration this verdict scored against still the device's configuration?
 *
 * The verdict stamps `configSnapshotIdAtRun` — an opaque `rawconfig:<md5>` that identifies the
 * snapshot without telling anyone anything about it. What a reviewer actually needs to know before
 * trusting a config-based verdict is narrower and more useful: was the config real and complete
 * (line count, capture time), and has the device moved on since.
 *
 * WHY "moved on" IS A WARNING RATHER THAN A NOTE. Automatic re-scoring is on by default:
 * ExposurePrefsHooks maintains deviceParsedConfigChanged/Created NOTIFICATION triggers that re-run
 * qualification whenever a new parse lands. So a verdict sitting behind the device's current config
 * is not the normal course of events — it means the re-score did not happen, or failed, or is still
 * inside the debounce window. That is an anomaly worth surfacing, not routine metadata.
 */

const SNAPSHOT_PREFIX = /^rawconfig:/;

/** The md5 out of a stamped snapshot id, or "" when the verdict cites none. */
function snapshotHash(entry) {
    const raw = entry && entry.configSnapshotIdAtRun;
    return typeof raw === "string" ? raw.replace(SNAPSHOT_PREFIX, "").trim() : "";
}

function newestSnapshot(snapshots) {
    return (snapshots || []).reduce(function (best, row) {
        if (!row || !row.hash) {
            return best;
        }
        return !best || (row.createdOn || 0) > (best.createdOn || 0) ? row : best;
    }, null);
}

/**
 * @param {Object} entry a stored qualification entry
 * @param {Array<{hash: String, createdOn: Number}>} snapshots the device's stored raw configs
 * @returns {{state: String, capturedAt: Number|null, detail: String}}
 *   state is one of:
 *     "none"        this verdict consulted no configuration at all
 *     "current"     it scored the config the device still has
 *     "superseded"  the device has been re-captured since — the verdict is behind
 *     "missing"     the snapshot it scored is no longer stored, so it cannot be re-read
 */
function describeSnapshot(entry, snapshots) {
    const hash = snapshotHash(entry);
    if (!hash) {
        return {
            state: "none",
            capturedAt: null,
            detail: "no configuration was consulted for this verdict",
        };
    }
    const rows = (snapshots || []).filter(function (row) {
        return row && row.hash;
    });
    const scored = rows.filter(function (row) {
        return row.hash === hash;
    })[0];
    if (!scored) {
        // Distinct from "superseded" on purpose: there the evidence can still be re-read, here it
        // cannot. A verdict whose config no longer exists can never be checked by hand again.
        return {
            state: "missing",
            capturedAt: null,
            detail:
                "the configuration this verdict scored is no longer stored on this device, so it " +
                "cannot be re-read — re-qualify to score against a current capture",
        };
    }
    const newest = newestSnapshot(rows);
    if (newest && newest.hash !== hash) {
        return {
            state: "superseded",
            capturedAt: scored.createdOn || null,
            detail:
                "this verdict scored an earlier configuration — the device has been captured " +
                "again since, and re-qualification should have followed. The Config tab shows " +
                "the NEWER configuration, not the one scored here",
        };
    }
    return {
        state: "current",
        capturedAt: scored.createdOn || null,
        detail: "this is the configuration the device currently holds — readable on the Config tab",
    };
}

/**
 * The facts that make an opaque snapshot id worth reading: how much was searched, and when the
 * device said it. Returns "" when neither is known, so a caller renders nothing rather than a
 * half-empty line.
 */
function describeProvenance(linesSearched, capturedAt, formatDate) {
    const parts = [];
    if (typeof linesSearched === "number" && linesSearched > 0) {
        parts.push(linesSearched + " lines");
    }
    if (capturedAt) {
        const when = formatDate ? formatDate(capturedAt) : "";
        if (when) {
            parts.push("captured " + when);
        }
    }
    return parts.join(" · ");
}

module.exports = {describeSnapshot, describeProvenance, snapshotHash};
